/**
 * Authentication Utility Functions
 *
 * Helper functions for OAuth authentication flow
 */
import type { UserSession } from '@/auth/sessionTypes';

export type AuthConfig = {
  authentication_id: string;
  protocol: 'oidc' | 'saml';
  provider: string;
  is_enabled: boolean;
  login: {
    auth_uri?: string;
    client_id?: string;
    redirect_uri?: string;
    start_url?: string;
  };
  theme?: Record<string, unknown>;
  identity_structure_mapping?: Record<string, unknown>;
};

export type AuthContext = {
  email: string;
  emailDomain: string;
  authenticationId: string;
  protocol: 'oidc' | 'saml';
  identity_structure_mapping?: Record<string, unknown>;
};

export type OAuthState = {
  email?: string;
  return_url?: string;
  [key: string]: unknown;
};

/**
 * Extract domain from email address
 * @param {string} emailAddress - Full email address
 * @returns {string} Domain portion of the email
 * @throws {Error} If email format is invalid
 */
export const extractEmailDomain = (emailAddress: string): string => {
  const parts = emailAddress.split('@');
  if (parts.length !== 2) {
    throw new Error('Invalid email format');
  }
  return parts[1];
};

/**
 * Validate email format
 * @param {string} email - Email address to validate
 * @returns {boolean} True if valid
 */
export const isValidEmail = (email?: string | null): boolean => {
  return !!(email && email.includes('@') && email.split('@')[1].length > 0);
};

/**
 * Build OAuth authorization URL
 * @param {Object} authConfig - Authentication provider configuration
 * @param {string} email - User's email address
 * @returns {string} Complete OAuth authorization URL
 */
export const buildAuthUrl = (authConfig: AuthConfig, email: string): string => {
  const redirectParams = new URLSearchParams({
    client_id: authConfig.login.client_id || '',
    response_type: 'code',
    redirect_uri: authConfig.login.redirect_uri || '',
    scope: 'openid profile email',
    state: btoa(
      JSON.stringify({
        email,
        return_url: window.location.origin,
      }),
    ),
  });

  return `${authConfig.login.auth_uri}?${redirectParams.toString()}`;
};

/**
 * Store authentication context in session storage
 * @param {string} email - User's email
 * @param {string} emailDomain - Email domain
 * @param {Object} authConfig - Authentication configuration
 */
export const storeAuthContext = (email: string, emailDomain: string, authConfig: AuthConfig): void => {
  const authContext: AuthContext = {
    email,
    emailDomain,
    authenticationId: authConfig.authentication_id,
    protocol: authConfig.protocol,
    identity_structure_mapping: authConfig.identity_structure_mapping,
  };

  sessionStorage.setItem('auth_context', JSON.stringify(authContext));
};

/**
 * Retrieve authentication context from session storage
 * @returns {Object|null} Auth context or null if not found
 */
export const getAuthContext = (): AuthContext | null => {
  try {
    const authContextStr = sessionStorage.getItem('auth_context');
    if (!authContextStr) {
      return null;
    }
    return JSON.parse(authContextStr) as AuthContext;
  } catch {
    return null;
  }
};

/**
 * Clear authentication context from session storage
 */
export const clearAuthContext = (): void => {
  sessionStorage.removeItem('auth_context');
};

/**
 * Store user session in local storage
 * @param {Object} sessionData - User session data
 */
export const storeUserSession = (sessionData: UserSession): void => {
  localStorage.setItem('user_session', JSON.stringify(sessionData));
  const token = sessionData.access_token || sessionData.id_token;
  if (token) {
    localStorage.setItem('access_token', token);
  } else {
    localStorage.removeItem('access_token');
  }

  if (sessionData.refresh_token) {
    localStorage.setItem('refresh_token', sessionData.refresh_token);
  } else {
    localStorage.removeItem('refresh_token');
  }
};

export const finalizeUserSession = (sessionData: UserSession): UserSession => {
  storeUserSession(sessionData);
  clearAuthContext();
  return sessionData;
};

/**
 * Clear all authentication data from storage
 */
export const clearAuthData = (): void => {
  localStorage.removeItem('access_token');
  localStorage.removeItem('refresh_token');
  localStorage.removeItem('user_session');
  sessionStorage.removeItem('auth_context');
};

/**
 * Check if user is authenticated
 * @returns {boolean} True if authenticated
 */
export const isAuthenticated = (): boolean => {
  const accessToken = localStorage.getItem('access_token');
  const userSession = localStorage.getItem('user_session');

  if (!userSession) {
    return false;
  }

  if (accessToken) {
    return true;
  }

  try {
    const parsedSession = JSON.parse(userSession) as UserSession;
    return parsedSession.auth_protocol === 'saml';
  } catch {
    return false;
  }
};

/**
 * Parse OAuth state parameter
 * @param {string} state - Base64 encoded state string
 * @returns {Object} Parsed state object
 */
export const parseOAuthState = (state: string): OAuthState => {
  try {
    return JSON.parse(atob(state)) as OAuthState;
  } catch {
    return {};
  }
};
